import React, { useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TOOLS } from "@/constants/testIds";
import { CheckCircle2, Circle } from "lucide-react";
import { Link } from "react-router-dom";

const QUESTIONS = [
  {
    id: "estado_civil",
    q: "¿Cuál es tu situación familiar?",
    opts: [
      { v: "soltero", label: "Soltero/a sin dependientes" },
      { v: "pareja", label: "En pareja" },
      { v: "familia", label: "Familia con hijos" },
      { v: "padres", label: "Soporte económico de padres" },
    ],
  },
  {
    id: "patrimonio",
    q: "¿Cuál de los siguientes describe mejor tu patrimonio?",
    opts: [
      { v: "inicial", label: "Estoy comenzando a construirlo" },
      { v: "intermedio", label: "Tengo activos pero sin estrategia" },
      { v: "consolidado", label: "Patrimonio consolidado, busco optimizar" },
      { v: "empresario", label: "Empresa propia / patrimonio empresarial" },
    ],
  },
  {
    id: "preocupacion",
    q: "¿Cuál es tu principal preocupación?",
    opts: [
      { v: "salud", label: "Un evento de salud mayor" },
      { v: "ingreso", label: "Que falte mi ingreso a mi familia" },
      { v: "retiro", label: "No llegar bien al retiro" },
      { v: "negocio", label: "La continuidad de mi negocio" },
    ],
  },
  {
    id: "objetivo",
    q: "¿Qué te gustaría lograr en los próximos 5 años?",
    opts: [
      { v: "ahorro", label: "Acumular un ahorro disciplinado" },
      { v: "patrimonio", label: "Hacer crecer mi patrimonio" },
      { v: "proteccion", label: "Blindar lo que ya tengo" },
      { v: "legado", label: "Estructurar un legado" },
    ],
  },
];

const RECOMENDACIONES = {
  salud: ["Salud", "Vida", "Ahorro"],
  ingreso: ["Vida", "Ahorro", "Salud"],
  retiro: ["Ahorro", "Inversiones", "Vida"],
  negocio: ["Empresas", "Vida", "Inversiones"],
};

const DEFAULT_RECOMENDACION = ["Vida", "Salud", "Ahorro"];
const getRecomendacion = (preocupacion) =>
  RECOMENDACIONES[preocupacion] || DEFAULT_RECOMENDACION;

const CoverageSimulator = () => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const recomendaciones = useMemo(
    () => getRecomendacion(answers.preocupacion),
    [answers.preocupacion]
  );

  const allAnswered = QUESTIONS.every((q) => answers[q.id]);

  if (submitted) {
    return (
      <div
        data-testid={TOOLS.coberturaResult}
        className="border border-razmek p-10 lg:p-16"
      >
        <div className="eyebrow text-razmek-gold">Resultado · Coberturas sugeridas</div>
        <h3 className="font-serif text-4xl lg:text-5xl text-razmek-dark mt-4">
          Tu prioridad recomendada:
        </h3>
        <ol className="mt-10 space-y-4">
          {recomendaciones.map((r, i) => (
            <li
              key={r}
              className="flex items-center justify-between border-b border-razmek pb-5"
            >
              <div className="flex items-center gap-6">
                <span className="font-serif text-3xl text-razmek-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif text-2xl text-razmek-dark">{r}</span>
              </div>
              <span className="eyebrow text-razmek-gray">
                {i === 0 ? "Máxima prioridad" : i === 1 ? "Alta" : "Complementaria"}
              </span>
            </li>
          ))}
        </ol>
        <div className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/precalificacion"
            className="bg-razmek-blue hover:bg-razmek-dark text-white px-7 py-4 font-sans tracking-[0.16em] uppercase text-[11px] border-2 border-razmek-blue hover:border-razmek-gold transition-all"
          >
            Avanzar a precalificación
          </Link>
          <Button
            variant="ghost"
            onClick={() => {
              setSubmitted(false);
              setAnswers({});
            }}
            className="rounded-none font-sans tracking-[0.16em] uppercase text-[11px]"
          >
            Reiniciar simulación
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div>
        <div className="eyebrow text-razmek-gold">Simulador de coberturas</div>
        <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-razmek-dark max-w-2xl">
          Responde 4 preguntas para conocer las
          <span className="italic text-razmek-blue"> coberturas prioritarias</span> en tu caso.
        </h2>
      </div>

      <div className="space-y-12">
        {QUESTIONS.map((q, qi) => (
          <div key={q.id} data-testid={TOOLS.coberturaPerfil + "-" + q.id}>
            <Label className="eyebrow text-razmek-gray">
              {String(qi + 1).padStart(2, "0")} · {q.q}
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {q.opts.map((o) => {
                const selected = answers[q.id] === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setAnswers((a) => ({ ...a, [q.id]: o.v }))}
                    className={`flex items-center gap-4 p-5 border-2 text-left transition-all ${
                      selected
                        ? "border-razmek-gold bg-razmek-gold/5"
                        : "border-razmek hover:border-razmek-blue"
                    }`}
                  >
                    {selected ? (
                      <CheckCircle2 size={20} className="text-razmek-gold shrink-0" />
                    ) : (
                      <Circle size={20} className="text-razmek-gray shrink-0" />
                    )}
                    <span className="font-serif text-lg text-razmek-dark">
                      {o.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Button
        disabled={!allAnswered}
        onClick={() => setSubmitted(true)}
        className="bg-razmek-blue hover:bg-razmek-dark text-white rounded-none border-2 border-razmek-blue hover:border-razmek-gold font-sans tracking-[0.14em] uppercase text-[11px] px-8 py-6"
      >
        Ver mis coberturas sugeridas
      </Button>
    </div>
  );
};

export default CoverageSimulator;
